import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms'
import { MatIcon } from '@angular/material/icon'
import { Router } from '@angular/router'
import { Title } from '@angular/platform-browser'
import { Auth, sendEmailVerification } from '@angular/fire/auth'

import { LoadingComponent } from '../../components/loading/loading.component'
import { ButtonComponent } from '../../components/button/button.component'
import { AuthService, MFA_SETTINGS_PATH } from '../../services/auth.service'
import { MfaService } from '../../services/mfa.service'
import { getTitle } from '../../util/accessibility'
import { invalidStringValidator } from '../../directives/validator/custom-validator'

const JAPANESE_PHONE_NUMBER_REGEX =
  /^[０0][7７8８9９][０0][-－]?[０-９0-9]{4}[-－]?[０-９0-9]{4}$/

@Component({
  selector: 'app-mfa-phone-setup-page',
  standalone: true,
  imports: [LoadingComponent, ButtonComponent, MatIcon, ReactiveFormsModule],
  templateUrl: './mfa-phone-setup-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mfa-phone-setup-page.component.scss',
})
export class MfaPhoneSetupPageComponent implements OnInit {
  isLoading = false
  phoneNumber = new FormControl('', {
    nonNullable: true,
    validators: [
      Validators.required,
      invalidStringValidator(JAPANESE_PHONE_NUMBER_REGEX),
    ],
  })

  setPhoneNumberForm = new FormGroup({
    phoneNumber: this.phoneNumber,
  })

  constructor(
    private auth: Auth,
    private router: Router,
    private authService: AuthService,
    private mfaService: MfaService,
    private titleService: Title,
  ) {
    this.titleService.setTitle(getTitle('電話番号の設定'))
  }

  ngOnInit() {
    if (!this.auth.currentUser) {
      this.router.navigate(['/login'])
    }
  }

  backToLogin() {
    // 設定画面から来た場合だけそこへ戻す。必須フローで /login へ戻るときは
    // 再ログイン後に元のページへ戻れるよう returnUrl を消費しない
    const fromSettings = this.authService.isFromMfaSettings()
    this.authService.clearMfaEnrollmentOrigin()
    if (fromSettings && this.auth.currentUser) {
      this.authService.consumeReturnUrl()
      this.router.navigate([MFA_SETTINGS_PATH])
      return
    }
    this.router.navigate(['/login'])
  }

  sendPhoneNumberSubmit = async (event: Event) => {
    event.preventDefault()

    if (this.setPhoneNumberForm.invalid) {
      return
    }

    const phoneNumber = this.toInternationalPhoneNumber(
      this.setPhoneNumberForm.value.phoneNumber || '',
    )
    const user = this.auth.currentUser

    if (user) {
      this.isLoading = true
      try {
        const verificationId = await this.mfaService.enrollPhoneNumber(
          user,
          phoneNumber,
        )
        this.authService.setPhoneNumber(phoneNumber)
        this.authService.setVerificationId(verificationId)

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        let errorMessage = 'エラーが発生しました。'
        if (error.code === 'auth/requires-recent-login') {
          this.isLoading = false
          if (this.authService.isFromMfaSettings()) {
            await this.authService.startReauthentication({ type: 'add-sms' })
          } else {
            await this.authService.signOutPreservingReturnUrl()
            this.authService.redirectToLogin({ reason: 'reauth' })
          }
          return
        } else if (error.code === 'auth/unverified-email') {
          errorMessage =
            'メールアドレスの確認が必要です。メールを確認して、リンクをクリックしてください。'
          await sendEmailVerification(user).catch((emailError) => {
            alert('メールの送信に失敗しました。再試行してください。')
            console.error('Error sending email verification:', emailError)
          })
        }
        alert(errorMessage)
        this.isLoading = false
        throw error
      }
    } else {
      this.router.navigate(['/login'])
      return
    }

    this.router.navigate(['/auth/phone-setup/verify'])
  }

  private toInternationalPhoneNumber(phoneNumber: string): string {
    const toHalfWidth = phoneNumber.replace(/[０-９]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 65248),
    )

    const digitsOnly = toHalfWidth.replace(/\D/g, '')
    if (digitsOnly.startsWith('0')) {
      return '+81' + digitsOnly.slice(1)
    }

    return phoneNumber
  }
}
